/**
 * <shipping-estimator> custom element.
 * Lets shoppers estimate shipping by ZIP from the cart drawer (this theme has no cart page).
 * Fetches live rates via Shopify's /cart/prepare_shipping_rates + /cart/async_shipping_rates
 * endpoints and updates the shipping row + estimated total rendered by cart-summary.
 * Markup and styles live in snippets/cart-shipping-estimator.liquid.
 */
class ShippingEstimator extends HTMLElement {
  constructor() {
    super();
    this._onCartUpdate = this._onCartUpdate.bind(this);
  }

  connectedCallback() {
    this.country = this.dataset.country || 'United States';
    this.savedZip = (this.dataset.savedZip || '').trim();
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.moneyFormatWithCurrency = this.dataset.moneyFormatWithCurrency || this.moneyFormat;
    this.currencyCodeEnabled = this.dataset.currencyCodeEnabled === 'true';
    this.freeText = this.dataset.freeText || 'FREE';
    this.shippingToTpl = this.dataset.shippingTo || 'Shipping to {zip}';

    this.triggerEl = this.querySelector('.se-trigger');
    this.formEl = this.querySelector('.se-form');
    this.summaryEl = this.querySelector('.se-summary');
    this.summaryTextEl = this.querySelector('.se-summary-text');
    this.inputEl = this.querySelector('.se-zip');
    this.estimateBtn = this.querySelector('.se-estimate-btn');
    this.changeBtn = this.querySelector('.se-change');
    this.clearBtn = this.querySelector('.se-clear');
    this.closeBtn = this.querySelector('.se-close');
    this.resetBtn = this.querySelector('.se-reset');
    this.errorEl = this.querySelector('.se-error');
    this.statusEl = this.querySelector('.se-status');

    this.captureStatic();

    if (this._bound) return;
    this._bound = true;

    this.triggerEl && this.triggerEl.addEventListener('click', () => this.showForm(this.activeZip()));
    this.changeBtn && this.changeBtn.addEventListener('click', () => this.showForm(this.activeZip()));
    this.clearBtn && this.clearBtn.addEventListener('click', () => this.revert());
    this.estimateBtn && this.estimateBtn.addEventListener('click', () => this.estimate(this.inputEl.value));
    this.resetBtn && this.resetBtn.addEventListener('click', () => this.reset());
    this.closeBtn && this.closeBtn.addEventListener('click', () => this.closeForm());
    this.inputEl && this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.estimate(this.inputEl.value); }
      else if (e.key === 'Escape') { e.preventDefault(); this.closeForm(); }
    });
    this.inputEl && this.inputEl.addEventListener('input', () => this.hideError());

    document.addEventListener('cart:update', this._onCartUpdate);

    // Only auto-apply an estimate the shopper explicitly made this session (stored override).
    // A saved customer address alone does NOT auto-estimate — shipping stays TBD until the
    // shopper estimates. The saved ZIP still prefills the input for convenience (see showForm).
    const override = this.getOverride();
    if (override && this.isValidZip(override)) {
      this.estimate(override);
    } else {
      this.showTrigger();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this._onCartUpdate);
  }

  _onCartUpdate() {
    this.captureStatic();
    // Re-apply only an estimate the shopper already made (stored override), so it survives
    // cart changes. If they haven't estimated, shipping stays TBD.
    const override = this.getOverride();
    if (override && this.isValidZip(override)) {
      // The cart morph repaints the shipping row back to TBD. Paint the loading state on the
      // next frame (after the morph, before the browser repaints) so the shopper sees
      // "Estimating…" rather than a flash of TBD while we re-fetch the rate.
      requestAnimationFrame(() => this.showShippingLoading());
      clearTimeout(this._reapplyTimer);
      this._reapplyTimer = setTimeout(() => this.estimate(override), 60);
    }
  }

  sessionKey() { return 'tomsShippingEstimateZip'; }

  getOverride() {
    try { return (sessionStorage.getItem(this.sessionKey()) || '').trim(); }
    catch (e) { return ''; }
  }

  setOverride(zip) {
    try {
      if (zip && zip !== this.savedZip) sessionStorage.setItem(this.sessionKey(), zip);
      else sessionStorage.removeItem(this.sessionKey());
    } catch (e) {}
  }

  clearOverride() {
    try { sessionStorage.removeItem(this.sessionKey()); } catch (e) {}
  }

  activeZip() { return this.getOverride() || this.savedZip; }

  isValidZip(zip) { return /^\d{5}(-\d{4})?$/.test((zip || '').trim()); }

  zipToProvince(zip) {
    const p = parseInt(String(zip || '').trim().slice(0, 3), 10);
    if (isNaN(p)) return '';
    const ranges = [
      [5, 5, 'NY'], [6, 9, 'PR'], [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'],
      [39, 49, 'ME'], [50, 54, 'VT'], [55, 55, 'MA'], [56, 59, 'VT'], [60, 69, 'CT'],
      [70, 89, 'NJ'], [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 205, 'DC'],
      [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'],
      [300, 319, 'GA'], [320, 349, 'FL'], [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'],
      [398, 399, 'GA'], [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
      [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'], [580, 588, 'ND'],
      [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'],
      [700, 714, 'LA'], [716, 729, 'AR'], [730, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'],
      [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'],
      [889, 898, 'NV'], [900, 961, 'CA'], [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'],
      [995, 999, 'AK']
    ];
    for (let i = 0; i < ranges.length; i++) {
      if (p >= ranges[i][0] && p <= ranges[i][1]) return ranges[i][2];
    }
    return '';
  }

  summaryRoot() {
    return this.closest('.cart-drawer__summary') || this.closest('.cart-drawer') || document;
  }

  shippingValueEl() { return this.summaryRoot().querySelector('.cart-shipping dd'); }
  totalValueEl() {
    const root = this.summaryRoot();
    return root.querySelector('.cart-estimated-total dd div') || root.querySelector('.cart-estimated-total dd');
  }

  captureStatic() {
    const shipEl = this.shippingValueEl();
    const totalEl = this.totalValueEl();
    if (shipEl && this._staticShip == null) this._staticShip = shipEl.textContent.trim();
    if (totalEl && this._staticTotal == null) this._staticTotal = totalEl.textContent.trim();
    if (this.getOverride() === '' && !this.isValidZip(this.savedZip)) {
      if (shipEl) this._staticShip = shipEl.textContent.trim();
      if (totalEl) this._staticTotal = totalEl.textContent.trim();
    }
  }

  showTrigger() {
    this.hide(this.formEl); this.hide(this.summaryEl); this.show(this.triggerEl);
    this.hideStatus(); this.hideError();
  }

  showForm(prefill) {
    this.hide(this.triggerEl); this.hide(this.summaryEl); this.show(this.formEl);
    if (this.inputEl) { this.inputEl.value = prefill || ''; this.inputEl.focus(); }
    if (this.resetBtn) this.resetBtn.hidden = !this.savedZip;
    this.hideError();
  }

  showSummary(zip) {
    this.hide(this.triggerEl); this.hide(this.formEl); this.show(this.summaryEl);
    if (this.summaryTextEl) this.summaryTextEl.textContent = this.shippingToTpl.replace('{zip}', zip);
  }

  // Dismiss the ZIP input without estimating. Return to the existing estimate summary if one
  // is active, otherwise back to the "Estimate shipping" trigger.
  closeForm() {
    const override = this.getOverride();
    if (override && this.isValidZip(override)) this.showSummary(override);
    else this.showTrigger();
  }

  // Show the loading label on the shipping row while a rate is being fetched.
  showShippingLoading() {
    const shipEl = this.shippingValueEl();
    if (shipEl) shipEl.textContent = this.dataset.loadingText || 'Estimating…';
  }

  reset() {
    this.clearOverride();
    this.restoreStatic();
    if (this.savedZip && this.isValidZip(this.savedZip)) this.estimate(this.savedZip);
    else this.showTrigger();
  }

  // Fully undo the estimate: drop the entered ZIP, restore the original shipping/total,
  // and return to the "Estimate shipping" trigger — regardless of any saved address.
  revert() {
    this.clearOverride();
    this.restoreStatic();
    this.showTrigger();
  }

  async estimate(zip) {
    zip = (zip || '').trim();
    if (!this.isValidZip(zip)) {
      this.show(this.formEl); this.hide(this.triggerEl); this.hide(this.summaryEl);
      this.showError(this.dataset.error);
      return;
    }
    this.setOverride(zip);
    this.showSummary(zip);
    this.showShippingLoading();
    this.showStatus(this.dataset.loadingText || 'Estimating…');
    if (this.estimateBtn) this.estimateBtn.disabled = true;

    try {
      const rates = await this.fetchRates(zip);
      if (!rates || !rates.length) {
        this.restoreStatic();
        this.showStatus(this.dataset.noRates);
        return;
      }
      this.applyRates(rates);
      this.hideStatus();
    } catch (e) {
      this.restoreStatic();
      this.showStatus(this.dataset.genericError);
    } finally {
      if (this.estimateBtn) this.estimateBtn.disabled = false;
    }
  }

  async fetchRates(zip) {
    const params = new URLSearchParams();
    params.append('shipping_address[zip]', zip);
    params.append('shipping_address[country]', this.country);
    const province = this.zipToProvince(zip);
    if (province) params.append('shipping_address[province]', province);
    const qs = params.toString();

    const prep = await fetch('/cart/prepare_shipping_rates.json?' + qs, { method: 'POST' });
    if (!prep.ok) {
      let detail = '';
      try { detail = ' ' + JSON.stringify(await prep.json()); } catch (e) {}
      throw new Error('prepare failed: ' + prep.status + detail);
    }

    for (let i = 0; i < 20; i++) {
      const res = await fetch('/cart/async_shipping_rates.json?' + qs);
      if (res.ok) {
        const data = await res.json();
        if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
      }
      await new Promise((r) => setTimeout(r, 500));
    }
    throw new Error('timeout');
  }

  applyRates(rates) {
    const cheapest = rates.reduce((a, b) => (parseFloat(a.price) <= parseFloat(b.price) ? a : b));
    const cents = Math.round(parseFloat(cheapest.price) * 100);

    const shipEl = this.shippingValueEl();
    // Force cents on the shipping value so it always reads e.g. $9.99, not $9.
    if (shipEl) shipEl.textContent = cents === 0 ? this.freeText : this.formatMoney(cents, { forceDecimals: true });

    const totalEl = this.totalValueEl();
    if (totalEl) {
      // Total keeps the exact same formatting cart-summary rendered it with
      // (money vs money_with_currency, no forced decimals) — unchanged.
      const base = parseInt(this.dataset.baseTotal || '0', 10);
      totalEl.textContent = this.formatMoney(base + cents, { withCurrency: this.currencyCodeEnabled });
    }
  }

  restoreStatic() {
    const shipEl = this.shippingValueEl();
    if (shipEl && this._staticShip != null) shipEl.textContent = this._staticShip;

    // Recompute the no-estimate total from the current base rather than a stale snapshot.
    // data-base-total is refreshed by the cart morph on every cart:update, so this stays
    // correct after the cart changes; using _staticTotal would restore the pre-update total.
    const totalEl = this.totalValueEl();
    if (totalEl) {
      const base = parseInt(this.dataset.baseTotal || '0', 10);
      totalEl.textContent = this.formatMoney(base, { withCurrency: this.currencyCodeEnabled });
    }
  }

  show(el) { if (el) el.hidden = false; }
  hide(el) { if (el) el.hidden = true; }
  showError(msg) { if (this.errorEl) { this.errorEl.textContent = msg || ''; this.errorEl.hidden = false; } }
  hideError() { if (this.errorEl) this.errorEl.hidden = true; }
  showStatus(msg) { if (this.statusEl) { this.statusEl.textContent = msg || ''; this.statusEl.hidden = false; } }
  hideStatus() { if (this.statusEl) this.statusEl.hidden = true; }

  formatMoney(cents, options) {
    options = options || {};
    const forceDecimals = options.forceDecimals;
    const format = options.withCurrency ? this.moneyFormatWithCurrency : this.moneyFormat;
    cents = Math.round(Number(cents));
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cts = parts[1] ? decimal + parts[1] : '';
      return dollars + cts;
    }
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(re);
    const key = match ? match[1] : 'amount';
    let value;
    // Honors the shop's money format by default. When forceDecimals is true, no-decimals
    // formats are rendered with 2 decimals instead (used for the estimated shipping value
    // so it keeps its cents, e.g. $9.99 not $9).
    switch (key) {
      case 'amount_no_decimals': value = withDelimiters(cents, forceDecimals ? 2 : 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, forceDecimals ? 2 : 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(re, value);
  }
}

if (!customElements.get('shipping-estimator')) {
  customElements.define('shipping-estimator', ShippingEstimator);
}
